import { useState } from "react";
import { Plus, Trash2, ImagePlus, FileText } from "lucide-react";
import { Btn } from "../../../components/shared/Btn";
import { formatINR } from "../../../lib/format";
import { configFor } from "../../../lib/propertyTypeSchema";
import { mediaApi } from "../../../lib/mediaApi";

/* Step 2 — BHK Configs. Each configuration carries carpet area, BUILT-UP
   area, price, units, and an optional floor-plan image. The floor plan is
   uploaded to the server immediately on pick (real URL stored on the
   config), then surfaced to the customer floor-plan viewer via
   bhkConfigs[].floorPlan. */
export function ConfigsStep({ data, setData, errors }) {
  const configs = data.configs || [];
  const setConfigs = (next) => setData({ ...data, configs: next });
  const [uploadingId, setUploadingId] = useState(null);

  const addConfig = () => {
    setConfigs([
      ...configs,
      { id: "cfg_" + Date.now(), bhk: (configFor(data.propertyType).configOptions || [""])[0] || "", carpetArea: "", builtUp: "", areaUnit: "sq_ft", price: "", priceLabel: "", units: "", floorPlan: "", servantRoom: false },
    ]);
  };
  const updateConfig = (id, patch) => setConfigs(configs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeConfig = (id) => setConfigs(configs.filter((c) => c.id !== id));

  async function handleFloorPlan(id, file) {
    if (!file) return;
    setUploadingId(id);
    try {
      const res = await mediaApi.uploadImage(file);
      if (res?.url) updateConfig(id, { floorPlan: res.url });
    } catch (err) {
      alert(err?.message || "Floor plan upload failed");
    } finally {
      setUploadingId(null);
    }
  }

  const fieldCls = "w-full text-sm rounded-md ring-1 ring-inset ring-slate-300 px-2.5 py-1.5 bg-white focus:outline-none focus:ring-blue-500";

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Configurations</h4>
          <p className="text-xs text-slate-500 mt-0.5">Add at least one configuration. Area &amp; price are required for each.</p>
        </div>
        <Btn variant="secondary" size="sm" onClick={addConfig}><Plus className="w-3.5 h-3.5" />Add configuration</Btn>
      </div>

      {errors.configs && <p className="text-[11px] text-red-600 mb-2">{errors.configs}</p>}

      {configs.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400 border border-dashed border-slate-300 rounded-xl">
          No configurations yet. Click <strong className="text-slate-600">Add configuration</strong> to start.
        </div>
      ) : (
        <div className="space-y-2">
          {configs.map((c, idx) => {
            const cfgSchema = configFor(data.propertyType);
            return (
            <div key={c.id} className="rounded-xl ring-1 ring-slate-200 bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700">Configuration {idx + 1}</span>
                <button onClick={() => removeConfig(c.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1 rounded-md transition-colors" title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                {!cfgSchema.hideConfigDropdown && (
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{cfgSchema.configLabel}</label>
                    <select value={c.bhk} onChange={(e) => updateConfig(c.id, { bhk: e.target.value })} className={fieldCls}>
                      {(cfgSchema.configOptions || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{data.propertyType === "Plot" ? "Plot Area" : "Carpet area"}</label>
                  <input type="number" value={c.carpetArea} onChange={(e) => updateConfig(c.id, { carpetArea: e.target.value })} placeholder="780" className={fieldCls + " tabular-nums"} />
                </div>
                {cfgSchema.showBuiltUp && (
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{data.propertyType === "Plot" ? "Build Area" : "Built-up area"}</label>
                    <input value={c.builtUp || ""} onChange={(e) => updateConfig(c.id, { builtUp: e.target.value })} placeholder="1180 sqft" className={fieldCls + " tabular-nums"} />
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Unit</label>
                  <select value={c.areaUnit} onChange={(e) => updateConfig(c.id, { areaUnit: e.target.value })} className={fieldCls}>
                    <option value="sq_ft">sq ft</option><option value="sq_m">sq m</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Price (₹)</label>
                  <input type="number" value={c.price} onChange={(e) => updateConfig(c.id, { price: e.target.value })} placeholder="8500000" className={fieldCls + " tabular-nums"} />
                  {c.price && <p className="text-[10px] text-slate-500 mt-0.5 tabular-nums">{formatINR(parseFloat(c.price))}</p>}
                </div>
                {cfgSchema.showUnits && (
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Units</label>
                    <input type="number" value={c.units} onChange={(e) => updateConfig(c.id, { units: e.target.value })} placeholder="60" className={fieldCls + " tabular-nums"} />
                  </div>
                )}
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">Price label (display)</label>
                  <input value={c.priceLabel} onChange={(e) => updateConfig(c.id, { priceLabel: e.target.value })} placeholder="₹85L onwards" className={fieldCls} />
                </div>
                {["Apartment", "Villa", "Duplex"].includes(data.propertyType) && (parseInt(String(c.bhk), 10) || 0) >= 3 && (
                  <div className="sm:col-span-3 flex items-center gap-2 pt-1">
                    <button type="button" role="switch" aria-checked={!!c.servantRoom}
                      onClick={() => updateConfig(c.id, { servantRoom: !c.servantRoom })}
                      className={"relative inline-flex h-5 w-9 items-center rounded-full transition-colors " + (c.servantRoom ? "bg-gold" : "bg-slate-300")}>
                      <span className={"inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform " + (c.servantRoom ? "translate-x-4" : "translate-x-1")} />
                    </button>
                    <span className="text-[11px] font-medium text-slate-600">With Servant Room</span>
                  </div>
                )}
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide block mb-1">{cfgSchema.planLabel}</label>
                  <div className="flex items-center gap-2">
                    {c.floorPlan ? (
                      <div className="flex items-center gap-2 min-w-0">
                        {/^[^?#]+\.pdf(\?|#|$)/i.test(c.floorPlan) ? (
                          <a href={c.floorPlan} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-[11px] text-blue-600 hover:underline truncate">
                            <FileText className="w-4 h-4 shrink-0 text-slate-400" />
                            View floor-plan PDF
                          </a>
                        ) : (
                          <a href={c.floorPlan} target="_blank" rel="noreferrer" className="shrink-0">
                            <img src={c.floorPlan} alt="Floor plan" className="w-9 h-9 rounded object-cover ring-1 ring-slate-200" />
                          </a>
                        )}
                        <button onClick={() => updateConfig(c.id, { floorPlan: "" })} className="text-[11px] text-red-600 hover:underline shrink-0">Remove</button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 rounded-md ring-1 ring-dashed ring-slate-300 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer">
                        <ImagePlus className="w-3.5 h-3.5 text-slate-400" />
                        {uploadingId === c.id ? "Uploading…" : "Upload floor plan"}
                        <input type="file" accept="image/*,.pdf" className="hidden" disabled={uploadingId === c.id}
                          onChange={(e) => handleFloorPlan(c.id, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
